class CustomAlgoliaMapPin extends HTMLElement {
  static activePin = null;
  static documentClickBound = false;
  static parsedBtgImages = new Map();

  constructor() {
    super();
    this.venueData = {};
    this.BTG_IMAGES = "[]";
    this.ROOT_URL = "/";
    this.MAP_PIN_IMAGE = "";
    this.MAP_PIN_CONFIG = { size: 23, iconSize: 13, activeScale: 1.15 };
    this.modalRendered = false;
    this.handlePinClick = this.handlePinClick.bind(this);
    this.handlePinKeydown = this.handlePinKeydown.bind(this);
  }

  connectedCallback() {
    this.render();
    this.addEventListener("click", this.handlePinClick);
    this.addEventListener("keydown", this.handlePinKeydown);

    if (!CustomAlgoliaMapPin.documentClickBound) {
      CustomAlgoliaMapPin.documentClickBound = true;
      document.addEventListener("click", CustomAlgoliaMapPin.handleDocumentClick);
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handlePinClick);
    this.removeEventListener("keydown", this.handlePinKeydown);

    if (CustomAlgoliaMapPin.activePin === this) {
      CustomAlgoliaMapPin.activePin = null;
    }
  }

  static handleDocumentClick(event) {
    if (!event.target?.closest?.("custom-algolia-map-pin")) {
      CustomAlgoliaMapPin.closeActivePin();
    }
  }

  static closeActivePin(exceptPin = null) {
    if (!CustomAlgoliaMapPin.activePin || CustomAlgoliaMapPin.activePin === exceptPin) return;
    CustomAlgoliaMapPin.activePin.closePin();
    CustomAlgoliaMapPin.activePin = null;
  }

  setPinData({ venueData, btgImages, rootUrl, mapPinImage, mapPinConfig } = {}) {
    this.venueData = venueData || {};
    this.BTG_IMAGES = btgImages || this.BTG_IMAGES;
    this.ROOT_URL = rootUrl || this.ROOT_URL;
    this.MAP_PIN_IMAGE = mapPinImage || this.MAP_PIN_IMAGE;
    this.MAP_PIN_CONFIG = mapPinConfig || this.MAP_PIN_CONFIG;

    if (this.isConnected) {
      this.render();
    }
  }

  render() {
    const venueId = this.getAttribute("data-venue-id");
    if (!venueId) return;

    if (!this.venueData?.id) {
      const widget = document.querySelector("algolia-search-widget");
      if (widget) {
        const dataStore = widget.getDataStore();
        this.venueData = dataStore.get(venueId) || {};
        this.BTG_IMAGES = widget.getBTGImages();
        this.ROOT_URL = widget.getCurrentRootURL();
        this.MAP_PIN_IMAGE = widget.getCoravinLogo();
        this.MAP_PIN_CONFIG = widget.getMapPinConfig?.() || this.MAP_PIN_CONFIG;
      }
    }

    if (!this.venueData?.id) return;

    this.modalRendered = false;
    this.style.setProperty("--map-pin-size", `${this.MAP_PIN_CONFIG.size}px`);
    this.style.setProperty("--map-pin-icon-size", `${this.MAP_PIN_CONFIG.iconSize}px`);
    this.style.setProperty("--map-pin-active-scale", this.MAP_PIN_CONFIG.activeScale);

    const { title, handle } = this.venueData;
    const { event_venue } = this.venueData.meta?.custom ?? {};
    const isEventVenue = event_venue === true;
    const productPath = `products/${handle}`;
    const productUrl = `${this.ROOT_URL}${this.ROOT_URL === "/" ? "" : "/"}${productPath}`;

    this.innerHTML = `
      <div role="button" tabindex="0" aria-label="${this.escapeAttribute(title)}" class="map-pin ${isEventVenue ? "event" : ""}">
        <img class="map-pin-logo" src="${this.escapeAttribute(this.MAP_PIN_IMAGE)}" loading="lazy" decoding="async" alt="" />
      </div>
      <a href="${this.escapeAttribute(productUrl)}" aria-label="View ${this.escapeAttribute(title)}">
        <div class="map-pin-modal"></div>
      </a>
    `;
  }

  handlePinClick(event) {
    const pinEl = event.target.closest(".map-pin");
    if (!pinEl) return;

    event.preventDefault();
    event.stopPropagation();

    if (pinEl.classList.contains("active")) {
      this.closePin();
      CustomAlgoliaMapPin.activePin = null;
      return;
    }

    CustomAlgoliaMapPin.closeActivePin(this);
    this.openPin();
    CustomAlgoliaMapPin.activePin = this;
  }

  handlePinKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    const pinEl = event.target.closest(".map-pin");
    if (!pinEl) return;

    event.preventDefault();
    pinEl.click();
  }

  openPin() {
    const pinEl = this.querySelector(".map-pin");
    const modalEl = this.ensureModalRendered();
    if (!pinEl || !modalEl) return;

    this.setOverlayActive(true);
    pinEl.classList.add("active");
    this.positionModal(pinEl, modalEl);
  }

  closePin() {
    this.querySelector(".map-pin")?.classList.remove("active");
    this.querySelector(".map-pin-modal")?.classList.remove("active");
    this.setOverlayActive(false);
  }

  ensureModalRendered() {
    const modalEl = this.querySelector(".map-pin-modal");
    if (!modalEl) return null;

    if (this.modalRendered) return modalEl;

    const { title } = this.venueData;
    const {
      rating,
      venue_type,
      region_city,
      wine_venue_ambiance
    } = this.venueData.meta?.custom ?? {};
    const subtitle = [venue_type, region_city].filter(Boolean).join(" - ");

    modalEl.innerHTML = `
      <div class="mapin-modal-content">
        ${this.venueData.image ? `
          <div class="mapin-modal-image">
            <img src="${this.escapeAttribute(this.venueData.image)}" loading="lazy" decoding="async" alt="" />
          </div>
        ` : ""}
        <div class="mapin-modal-details">
          ${this._renderRating(rating)}
          <span class="mapin-modal-details--title">${this.escapeHTML(title)}</span>
          ${subtitle ? `<span class="mapin-modal-details--subtitle">${this.escapeHTML(subtitle)}</span>` : ""}
          ${wine_venue_ambiance?.length ? `<span class="mapin-modal-details--text medium">${this.escapeHTML(wine_venue_ambiance[0])}</span>` : ""}
        </div>
      </div>
    `;

    this.modalRendered = true;
    return modalEl;
  }

  _renderRating(rating = null) {
    if (rating === null) return "";

    const btgImages = this.getBtgImages();
    if (!btgImages.length) return "";

    const src = btgImages.at(rating - 1);
    if (src) {
      const CDNUrl = window.ShopifyCDNHelper.getCDNUrl(src);
      return `<img style="max-width: ${rating == 1 ? "8px" : rating == 2 ? "18px" : "15px"};" src="${this.escapeAttribute(CDNUrl)}" loading="lazy" decoding="async" alt="" />`;
    }

    return "";
  }

  getBtgImages() {
    const cacheKey = typeof this.BTG_IMAGES === "string"
      ? this.BTG_IMAGES
      : JSON.stringify(this.BTG_IMAGES || []);

    if (CustomAlgoliaMapPin.parsedBtgImages.has(cacheKey)) {
      return CustomAlgoliaMapPin.parsedBtgImages.get(cacheKey);
    }

    let btgImages = [];
    try {
      btgImages = typeof this.BTG_IMAGES === "string" ? JSON.parse(this.BTG_IMAGES) : this.BTG_IMAGES;
    } catch (e) {}

    CustomAlgoliaMapPin.parsedBtgImages.set(cacheKey, btgImages || []);
    return btgImages || [];
  }

  setOverlayActive(isActive) {
    const overlay = this.closest(".custom-algolia-map-pin-overlay");
    if (!overlay) return;

    overlay.classList.toggle("active", isActive);
    overlay.style.zIndex = isActive ? "1000" : "1";
  }

  positionModal(pinEl, modalEl) {
    const mapRect = document.querySelector(".ais-GeoSearch-map").getBoundingClientRect();
    const pinRect = pinEl.getBoundingClientRect();

    const prevDisplay = modalEl.style.display;
    const prevVisibility = modalEl.style.visibility;
    modalEl.style.visibility = "hidden";
    modalEl.style.display = "block";
    const modalRect = modalEl.getBoundingClientRect();
    modalEl.style.display = prevDisplay;
    modalEl.style.visibility = prevVisibility;

    const space = {
      top: pinRect.top - mapRect.top,
      bottom: mapRect.bottom - pinRect.bottom,
      left: pinRect.left - mapRect.left,
      right: mapRect.right - pinRect.right
    };

    let position = "bottom";

    if (space.bottom >= modalRect.height) {
      position = "bottom";
    } else if (space.top >= modalRect.height) {
      position = "top";
    } else if (space.right >= modalRect.width) {
      position = "right";
    } else if (space.left >= modalRect.width) {
      position = "left";
    } else {
      const [bestSide] = Object.entries(space).sort(([, a], [, b]) => b - a);
      if (bestSide) position = bestSide;
    }

    modalEl.dataset.position = position;
    modalEl.classList.add("active");
  }

  escapeHTML(value = "") {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  escapeAttribute(value = "") {
    return this.escapeHTML(value);
  }
}

customElements.define("custom-algolia-map-pin", CustomAlgoliaMapPin);
